import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PROFILE } from "@/constants/testIds";
import { Bookmark, MessageSquare, LogOut, BookOpenCheck, Pencil, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import ProfileSecurity from "@/components/ProfileSecurity";

function formatRead(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const day = 86_400_000;
  if (diffMs < day) return "Today";
  if (diffMs < 2 * day) return "Yesterday";
  if (diffMs < 7 * day) return `${Math.floor(diffMs / day)} days ago`;
  return d.toLocaleDateString();
}

export default function Profile() {
  const { user, loading, logout, refresh } = useAuth();
  const [stats, setStats] = useState({ bookmarks: 0, comments: 0 });
  const [bookmarks, setBookmarks] = useState({ blogs: [], pathways: [] });
  const [recentReads, setRecentReads] = useState([]);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/me/stats").then(r => setStats(r.data));
    api.get("/bookmarks").then(r => setBookmarks(r.data));
    api.get("/reads/recent?limit=8").then(r => setRecentReads(r.data.items || [])).catch(() => {});
  }, [user]);

  const startEdit = () => { setNameDraft(user.name || ""); setEditingName(true); };
  const cancelEdit = () => { setEditingName(false); setNameDraft(""); };
  const saveName = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) { toast.error("Name can't be empty"); return; }
    if (trimmed === user.name) { setEditingName(false); return; }
    setSaving(true);
    try {
      await api.put("/me/profile", { name: trimmed });
      await refresh();
      toast.success("Name updated");
      setEditingName(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update name");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-12 text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;

  return (
    <div data-testid={PROFILE.page} className="max-w-4xl mx-auto px-6 md:px-10 py-16">
      <div className="text-xs uppercase tracking-[0.24em] text-sky-700 mb-3">Your account</div>
      <div className="flex items-center gap-5">
        {user.picture
          ? <img src={user.picture} alt="" className="w-16 h-16 rounded-full border border-slate-200"/>
          : <div className="w-16 h-16 rounded-full bg-slate-200"/>}
        <div className="min-w-0">
          {editingName ? (
            <div className="flex items-center gap-2">
              <Input data-testid="profile-name-input"
                     value={nameDraft}
                     onChange={e => setNameDraft(e.target.value)}
                     onKeyDown={e => { if (e.key === "Enter") saveName(); if (e.key === "Escape") cancelEdit(); }}
                     maxLength={80}
                     autoFocus
                     className="font-heading text-2xl font-semibold text-slate-900 h-10 max-w-sm"/>
              <button data-testid="profile-name-save" onClick={saveName} disabled={saving}
                      className="p-2 rounded-md bg-sky-700 hover:bg-sky-600 text-white disabled:opacity-60"
                      aria-label="Save">
                <Check className="w-4 h-4"/>
              </button>
              <button data-testid="profile-name-cancel" onClick={cancelEdit}
                      className="p-2 rounded-md border border-slate-300 hover:bg-slate-50 text-slate-600"
                      aria-label="Cancel">
                <X className="w-4 h-4"/>
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <h1 data-testid="profile-name" className="font-heading text-3xl font-semibold text-slate-900">{user.name}</h1>
              <button data-testid="profile-name-edit" onClick={startEdit}
                      className="p-1.5 rounded-md text-slate-400 hover:text-sky-700 hover:bg-slate-100 transition-colors"
                      title="Edit name" aria-label="Edit name">
                <Pencil className="w-4 h-4"/>
              </button>
            </div>
          )}
          <div className="text-sm text-slate-500">{user.email}</div>
          <div className="mt-1 inline-block text-[10px] font-bold uppercase tracking-[0.2em] text-sky-700 bg-sky-50 px-2 py-0.5 rounded">{user.role}</div>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-3 gap-4 max-w-xl">
        <StatCard icon={<Bookmark className="w-4 h-4"/>} label="Bookmarks" value={stats.bookmarks}/>
        <StatCard icon={<MessageSquare className="w-4 h-4"/>} label="Comments" value={stats.comments}/>
        <StatCard icon={<BookOpenCheck className="w-4 h-4"/>} label="Blogs read" value={recentReads.length + (recentReads.length === 8 ? "+" : "")}/>
      </div>

      <section className="mt-12" data-testid="profile-recent-reads">
        <h2 className="font-heading text-xl font-semibold text-slate-800 mb-3">Recently read</h2>
        {recentReads.length === 0
          ? <div className="text-sm text-slate-500">You haven&apos;t finished any blogs yet.</div>
          : <ul className="divide-y border-y border-slate-200">
              {recentReads.map(r => (
                <li key={r.blog_id} data-testid={`recent-read-${r.blog_id}`}>
                  <Link to={`/blogs/${r.slug}`} className="flex items-start justify-between gap-4 py-3 hover:bg-slate-50 px-2 -mx-2 rounded">
                    <div className="min-w-0 flex-1">
                      <div className="font-heading font-semibold text-slate-900">{r.title}</div>
                      <div className="text-sm text-slate-500 line-clamp-1">{r.excerpt}</div>
                    </div>
                    <span className="shrink-0 text-xs uppercase tracking-widest text-slate-400 pt-1 tabular-nums">
                      {formatRead(r.read_at)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>}
      </section>

      <section className="mt-12">
        <h2 className="font-heading text-xl font-semibold text-slate-800 mb-3">Saved blogs</h2>
        {bookmarks.blogs.length === 0
          ? <div className="text-sm text-slate-500">Nothing saved yet.</div>
          : <ul className="divide-y border-y border-slate-200">
              {bookmarks.blogs.map(b => (
                <li key={b.blog_id}>
                  <Link to={`/blogs/${b.slug}`} className="block py-3 hover:bg-slate-50">
                    <div className="font-heading font-semibold text-slate-900">{b.title}</div>
                    <div className="text-sm text-slate-500 line-clamp-1">{b.excerpt}</div>
                  </Link>
                </li>
              ))}
            </ul>}
      </section>

      <div className="mt-12">
        <ProfileSecurity user={user} />
      </div>

      <div className="mt-12">
        <Button onClick={logout} variant="outline" className="border-slate-300 text-red-600">
          <LogOut className="w-4 h-4 mr-2"/> Sign out
        </Button>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4">
      <div className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-2">{icon} {label}</div>
      <div className="mt-1 font-heading text-3xl font-semibold text-slate-900">{value}</div>
    </div>
  );
}
